import { ApiService } from './api.service'
import { CONFIG } from '~/services/config'

class AuthService {
  constructor () {
    this.request = new ApiService()
  }

  /*
     * API service for checking for user existence
     * @param params (email or uuid)
     * @returns {*}
     */
  userExistenceCheck = params => {
    return this.request.get('/user', {
      params: params
    })
  }

  /*
     * User Registration API service
     * @param formData
     * @returns {*}
     */
  register = formData => {
    return this.request.post('/register', formData)
  }

  /*
     * User Login API service
     * @param formData
     * @returns {*}
     */
  login = formData => {
    return this.request.post('/login', formData)
  }

  /*
     * User Profile API service
     * @param formData
     * @returns {*}
     */
  profile = formData => {
    return this.request.get('/profile', { params: formData })
  }

  /*
     * Send device info API service
     * Used for app analytics and device tracking (iOS & Android)
     * @param payload - Device information object
     * @returns {*}
     */
  sendDeviceInfo = payload => {
    return this.request.post('/user/device-info', payload)
  }

  /*
     * google auth API service
     * @param formData
     * @returns {*}
     */
  googleAuthentication = formData => {
    this.setSourcePage(formData.state, '/authorization/google/login')
    return this.request.get('/authorization/google/login', { params: formData })
  }

  /*
     * Facebook auth API service
     * @param formData
     * @returns {*}
     */
  facebookAuthentication = formData => {
    this.setSourcePage(formData.state, '/authorization/facebook/login')
    return this.request.get('/authorization/facebook/login', { params: formData })
  }

  /*
     * LinkedIn auth API service
     * @param formData
     * @returns {*}
     */
  linkedinAuthentication = formData => {
    this.setSourcePage(formData.state, '/authorization/linkedin/login')
    return this.request.get('/authorization/linkedin/login', { params: formData })
  }

  /*
     * Apple auth API service
     * @param formData
     * @returns {*}
     */
  appleAuthentication = formData => {
    this.setSourcePage(formData.state, '/authorization/apple/login')
    return this.request.get('/authorization/apple/login', { params: formData })
  }

  /*
     * Social auth submission API service
     * @param formData
     * @returns {*}
     */
  submitSocialAuth = formData => {
    const params ={
      code: formData.code, state: formData.state
    }

    if (formData?.origin === 'ios') {
      params.origin = 'ios'
    }

    return this.request.get('/authorization/social/callback', {
      params: params
    })
  }

  /*
     * forgot password API service
     * @param formData
     * @returns {*}
     */
  forgotPassword = formData => {
    return this.request.post('/forgot-password', formData)
  }

  /*
     * Reset password API service
     * @param url
     * @param formData
     * @returns {*}
     */
  resetPassword = (url, formData) => {
    return this.request.post(url, formData)
  }

  /*
     * API service for checking for url validity
     * @param url
     * @returns {*}
     */
  resetPasswordCheck = url => {
    return this.request.get(url)
  }

  /*
     * Logout API service
     * @returns {*}
     */
  logout = (refreshToken = '') => {
    // The refresh token lets the backend revoke the session at Descope (CLUB-946).
    // Optional: a session without one still logs out locally.
    return this.request.delete('/logout', refreshToken ? { data: { refresh_token: refreshToken } } : {})
  }

  /*
     * Profile update API service
     * @param formData
     * @returns {*}
     */
  updateProfile = formData => {
    return this.request.patch('/profile', formData)
  }

  /*
     * Profile/Descope sync API service — no body; Member Hub resolves the caller from the
     * request's own auth token, fetches their current Descope state server-side, and
     * updates its own profile record to match (email/phone verified status). Response is
     * profile-shaped (plus a few extra properties), so no separate profile() refetch is
     * needed after calling this.
     * @returns {*}
     */
  syncProfile = () => {
    return this.request.post('/profile/sync')
  }

  /*
     * Profile password update API service
     * @param formData
     * @returns {*}
     */
  updateProfilePassword = formData => {
    return this.request.patch('/profile/update-password', formData)
  }

  /*
     * Delete account API service
     * @param formData
     * @returns {*}
   */
  deleteAccount = formData => {
    return this.request.post('/profile/delete', formData)
  }

  /*
     * Resent email user verification
     * @returns {*}
     */
  resendVerification = () => {
    return this.request.get('/resend-verification')
  }

  /*
     * Google one tap authentication API service
     * @param formData
     * @returns {*}
     */
  googleOneTapAuthentication = formData => {
    this.setSourcePage(formData.state, '/authorization/google/one-tap')
    return this.request.get('/authorization/google/one-tap', {
      params: {
        state: formData.state,
        id_token: formData.id_token
      }
    })
  }

  /*
     * Refresh token API service
     * @returns {*}
     */
  refreshToken = () => {
    const token = getLocal('token') ? JSON.parse(getLocal('token')) : {}
    const refreshToken = token.refresh_token
    if (!refreshToken) {
      return Promise.reject(new Error('No refresh token available'))
    }
    return this.request.post(`/refresh-token/${encodeURIComponent(refreshToken)}`)
  }

  /*
     * Verify User with url itself API service
     * @param url
     * @returns {*}
     */
  verifyUser = url => {
    return this.request.post(url)
  }

  /*
     * Source page param rendering for SSO tracking
     * @param path
     * @returns {`${string}?ssoUrl=${*}${string}`}
     */
  setSourcePage = (state, path) => {
    const { environment } = useRuntimeConfig().public
    const route = useRoute()
    const utms = useUtmsStore()
    sessionStorage.setItem(state, JSON.stringify({
      source_page_url: location.href,
      sso_url: `${CONFIG.baseUrl(environment, 'memberhub')}${path}`,
      query: route.query,
      sso_path: path.replace('/authorization/', ''),
      source_page_path: location.pathname,
      utms: utms.utms
    }))
  }

  /*
     * Returns the logged in user's details
     * @returns {*}
     */
  userDetails = () => {
    return this.request.get('/user/details')
  }

  /*
   * Registers a guest user.
   *
   * @param {Object} formData - The form data for guest registration.
   * @returns {Promise} - The API response.
   */
  guestRegister = formData => {
    return this.request.post('/guest/register', formData)
  }

  /*
   * Guest Password Reset API service
   *
   * This method sends a POST request to the '/guest/password/reset' endpoint
   * to reset the password for a guest user.
   *
   * @param {Object} formData - The form data containing the necessary information for password reset.
   * @returns {Promise} - The API response.
   */
  guestPasswordReset = formData => {
    return this.request.post('/guest/password/reset', formData)
  }
  /*
   * Update Apple relay email to real email and link records
   * @param {Object} formData - { email: 'real@email.com' }
   * @returns {Promise} - The API response
   */
  updateAppleRelayEmail = formData => {
    return this.request.post('/authorization/apple/update-email', formData)
  }
  /*
   * Mark vault modal as triggered
   * @param {number} vaultId - The vault ID
   * @returns {Promise} - The API response
   */
  markModalTriggered = vaultId => {
    return this.request.patch('/profile/modal-triggered', {
      vault_id: vaultId
    })
  }
}

export default AuthService
